import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";

const CLIENT_TIMEOUT_S = 90;
const MEDIA_TIMEOUT_S = 15 * 60;

const STEPS = [
  { t: "Reading & transcribing your material", done: (s) => s.generation_status !== "pending" && s.generation_status !== "extracting" },
  { t: "Composing notes & summaries", done: (s) => s.progress.includes("notes") && s.progress.includes("summaries") },
  { t: "Building flashcards & quiz", done: (s) => s.progress.includes("practice") },
  { t: "Compiling glossary & exam forecast", done: (s) => s.progress.includes("extras") },
  { t: "Saving your study", done: (s) => s.generation_status === "ready" },
];

const elapsedSince = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));

export default function GenerationStatus({ sc, onRetried }) {
  const nav = useNavigate();
  const [elapsed, setElapsed] = useState(() => elapsedSince(sc.generation_started_at || sc.created_at));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setElapsed(elapsedSince(sc.generation_started_at || sc.created_at)), 1000);
    return () => clearInterval(t);
  }, [sc.generation_started_at, sc.created_at]);

  const failed = sc.generation_status === "failed";
  const hasMedia = (sc.sources || []).some((s) => ["audio", "video", "youtube", "recording"].includes(s.type));
  const limit = hasMedia ? MEDIA_TIMEOUT_S : CLIENT_TIMEOUT_S;
  const timedOut = !failed && elapsed >= limit;
  const view = { ...sc, progress: sc.generation_progress || [] };
  const activeIdx = STEPS.findIndex((s) => !s.done(view));

  const retry = async () => {
    setBusy(true);
    try {
      await api.post(`/subcourses/${sc.id}/retry`);
      toast.success("Retrying generation…");
      onRetried();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) { onRetried(); return; }
      toast.error(e?.response?.data?.detail || "Couldn't retry.");
      if (status === 400) { await api.delete(`/subcourses/${sc.id}`).catch(() => {}); nav("/app/studies/new"); }
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    await api.delete(`/subcourses/${sc.id}`).catch(() => {});
    toast.success("Generation cancelled");
    nav("/app/studies/new");
  };

  return (
    <div className="surface p-7 md:p-8 relative overflow-hidden" data-testid="gen-status-card">
      {failed || timedOut ? (
        <div data-testid={failed ? "gen-failed" : "gen-timeout"}>
          <h3 className="text-[18px] font-semibold text-[#B8A4FF] mb-2">
            {failed ? "Generation failed" : "This is taking longer than expected"}
          </h3>
          <p className="text-[14px] text-[#B0AEBC] mb-5" data-testid="gen-error-text">
            {failed
              ? (sc.generation_error || "Something went wrong. Please retry.")
              : `We haven't heard back in ${hasMedia ? "15 minutes" : "90 seconds"}. You can keep waiting or cancel — it'll auto-fail with a Retry button if it's truly stuck.`}
          </p>
          <div className="flex flex-wrap gap-2.5">
            {failed && (
              <button onClick={retry} disabled={busy} className="btn btn-primary" data-testid="gen-retry-btn">Retry</button>
            )}
            <button onClick={cancel} disabled={busy} className="btn btn-secondary" data-testid="gen-cancel-btn">Cancel</button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between gap-3 mb-5">
            <span className="text-[17px] font-semibold" data-testid="gen-stage-heading">
              {STEPS[Math.max(0, activeIdx)].t}…
            </span>
            <span className="text-[12px] text-[#8E8B99] tabular-nums" data-testid="gen-elapsed">{elapsed}s</span>
          </div>
          <ol className="space-y-2.5 text-[13px]">
            {STEPS.map((s, i) => {
              const done = s.done(view);
              const active = i === activeIdx;
              return (
                <li key={s.t} className="flex items-center gap-2.5" data-testid={`gen-step-${i}`}>
                  {active ? (
                    <motion.span animate={{ scale: [1, 1.4, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}
                      className="w-2 h-2 rounded-full bg-[#7C5CFF]" />
                  ) : (
                    <span className="w-2 h-2 rounded-full" style={{ background: done ? "#5AC8FA" : "rgba(128,128,140,0.3)" }} />
                  )}
                  <span className={active ? "font-medium" : done ? "text-[#8E8B99] line-through" : "text-[#8E8B99]"}>{s.t}</span>
                </li>
              );
            })}
          </ol>
          <div className="flex justify-end mt-6">
            <button onClick={cancel} disabled={busy} className="text-[12.5px] text-[#8E8B99] hover:text-[#B8A4FF]" data-testid="gen-cancel-inline-btn">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
